/**
 * News Detail popup (Sprint 3a) - tap a Market News item to open a comment view.
 * Shows: the headline, deterministic seeded NPC comments (from the instanceId),
 * static stance choices the player can tap, and the player's posted stances with
 * mood-matched NPC replies delivered a few seconds later by commentSystem.
 * Pure flavor - stances have no market impact. Text-only memes (no emoji).
 */
import { COINS } from '../game/config/coins.js';
import { STANCE_CHOICES, NPC_NICKS, seedComments } from '../game/config/comments.js';
import type { FeedNewsItem } from '../game/infoState.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { getState, dispatch, requestRender } from './renderer.js';
import { newsText, companyTagHtml } from './newsFeedPanel.js';

let overlay: HTMLElement | null = null;
let openInstanceId: string | null = null;
let pollTimer: number | null = null;

function coinName(coinId: string): string {
  const cfg = COINS.find(c => c.id === coinId);
  return cfg ? t(cfg.nameKey) : coinId;
}

/** Comment meme pool key for the news direction (cmt_up_N / cmt_down_N). */
function memeKey(direction: 1 | -1, textIdx: number): string {
  return `cmt_${direction === 1 ? 'up' : 'down'}_${textIdx + 1}`;
}

function replyText(replyIdx: number, mood?: string): string {
  return mood ? t(`npc_reply_${mood}_${replyIdx + 1}`) : t(`npc_reply_${replyIdx + 1}`);
}

function commentRow(nick: string, text: string, ago: string, mine = false): string {
  return `<div class="ncm-row${mine ? ' ncm-mine' : ''}">
    <span class="ncm-nick">${nick}</span>
    <span class="ncm-text">${text}</span>
    <span class="ncm-ago">${ago}</span>
  </div>`;
}

function agoLabel(minutes: number): string {
  return minutes <= 0 ? t('news_now') : `${minutes}m`;
}

function elapsedFromNow(at: number): string {
  const sec = Math.max(0, Math.floor((Date.now() - at) / 1000));
  if (sec < 60) return t('news_now');
  return `${Math.floor(sec / 60)}m`;
}

/** Build the ordered comment list: seeded NPCs, then the player's stances + replies. */
function renderComments(news: FeedNewsItem): string {
  const info = getState().info;
  const seeded = seedComments(news.instanceId);
  let html = '';
  for (const c of seeded) {
    html += commentRow(NPC_NICKS[c.nickIdx], t(memeKey(news.direction, c.textIdx)), agoLabel(c.agoMin));
  }

  const mine = info.playerComments.filter(p => p.instanceId === news.instanceId);
  for (const p of mine) {
    const text = p.stanceIdx !== undefined && STANCE_CHOICES[p.stanceIdx]
      ? t(STANCE_CHOICES[p.stanceIdx].key)
      : p.text;
    html += commentRow(t('ncm_you'), text, elapsedFromNow(p.at), true);
  }
  for (const r of info.npcReplies.filter(x => x.instanceId === news.instanceId)) {
    html += commentRow(NPC_NICKS[r.nickIdx], replyText(r.replyIdx, r.mood), elapsedFromNow(r.at));
  }
  return html;
}

function stanceButtons(): string {
  return STANCE_CHOICES
    .map((s, i) => `<button class="ncm-stance" data-stance="${i}">${t(s.key)}</button>`)
    .join('');
}

function render() {
  if (!overlay || !openInstanceId) return;
  const news = getState().info.feed.find(
    f => f.kind === 'news' && f.instanceId === openInstanceId
  ) as FeedNewsItem | undefined;
  if (!news) { close(); return; }

  const body = overlay.querySelector('.ncm-body');
  const list = overlay.querySelector('.ncm-list');
  if (body) {
    body.innerHTML = `${companyTagHtml(news.newsId)}${newsText(news.newsId, news.coinId)}`;
  }
  if (list) list.innerHTML = renderComments(news);
}

function onStance(stanceIdx: number) {
  if (!openInstanceId) return;
  playSfx('tap');
  dispatch({ type: 'PICK_NEWS_STANCE', payload: { instanceId: openInstanceId, stanceIdx } });
  render();
}

export function openNewsDetail(instanceId: string) {
  const news = getState().info.feed.find(f => f.kind === 'news' && f.instanceId === instanceId);
  if (!news || news.kind !== 'news') return;
  openInstanceId = instanceId;

  overlay?.remove();
  overlay = document.createElement('div');
  overlay.className = 'modal-overlay ncm-overlay';
  overlay.innerHTML = `<div class="ncm-modal exchange-theme">
    <div class="ncm-head">
      <span class="ncm-title">${t('ncm_title')} · ${coinName(news.coinId)}</span>
      <button class="ncm-close" data-close>&times;</button>
    </div>
    <div class="ncm-body"></div>
    <div class="ncm-list"></div>
    <div class="ncm-stance-label">${t('ncm_your_take')}</div>
    <div class="ncm-stance-row">${stanceButtons()}</div>
  </div>`;

  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('[data-close]')?.addEventListener('click', close);
  overlay.querySelectorAll('[data-stance]').forEach(btn => {
    btn.addEventListener('click', () => onStance(Number((btn as HTMLElement).dataset.stance)));
  });
  document.body.appendChild(overlay);
  render();

  // poll so scheduled NPC replies appear while the popup is open
  if (pollTimer === null) pollTimer = window.setInterval(render, 1000);
}

export function close() {
  if (pollTimer !== null) { clearInterval(pollTimer); pollTimer = null; }
  overlay?.remove();
  overlay = null;
  openInstanceId = null;
  requestRender();
}
